import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  changePassword as apiChangePassword,
  fetchSession,
  login,
  logout,
  startImpersonation as apiStartImpersonation,
  stopImpersonation as apiStopImpersonation,
  type MapRegion,
  type Role,
  type ProfileIndicator,
  type SessionInfo,
} from '@/api/client'
import { extractApiError } from '@/api/errors'
import {
  canAdminister as canAdministerWithRole,
  canOperate as canOperateWithRole,
} from '@/lib/capabilities'
import { hasFeature, type FeatureFlag } from '@/lib/featureAccess'
import { hardNavigate } from '@/lib/hardNavigate'

/**
 * Auth state for the server-side session gate. The session itself lives in an
 * HttpOnly cookie owned by the backend — this store only mirrors who is logged
 * in (email + role) so the router guard and the topbar can react without
 * re-probing the API. `mustChangePassword` mirrors the backend's forced-change
 * flag so the guard can confine the user to the change-password screen.
 */
export const useAuthStore = defineStore('auth', () => {
  // state
  const email = ref<string | null>(null)
  const role = ref<Role | null>(null)
  const mustChangePassword = ref(false)
  // Read-only mode. Set from EVERY session response, including the boot probe:
  // set it only on sign-in and the banner survives until the first reload, then
  // silently vanishes while the API keeps refusing writes.
  const tenantSuspended = ref(false)
  // The PLATFORM axis, disjoint from `role`. Never derived from the ladder and
  // never combined with it: `canAdminister` answers a question about a
  // customer's own hierarchy, this one about whether the session is ours.
  //
  // FALSE WHILE IMPERSONATING, because the backend says so — inside a window
  // the session resolves to the customer's principal and every platform route
  // 403s. Mirroring that verbatim is what makes the panel disappear from the
  // navigation at exactly the moment it stops working.
  const isPlatformAdmin = ref(false)
  // The impersonation window, for the banner P12 draws. Kept as the ISO string
  // the API sends rather than a Date: the store's job is to mirror the session,
  // and parsing here would make two places responsible for the timezone.
  // The product modules this session has (B10). Mirrored from EVERY session
  // response for the same reason `tenantSuspended` is: set it only on sign-in
  // and an admin flipping a flag mid-session leaves the UI offering a module
  // the API now refuses.
  const features = ref<FeatureFlag[]>([])
  // Which indicators this session may see (B5), in the profile's order, and
  // mirrored from every session response for the same reason `features` is.
  // Kept RAW: narrowing to the frontend's union is `indicatorFilter`'s job, and
  // doing it here would strand the anomaly texts from their keys.
  const profileIndicators = ref<ProfileIndicator[]>([])
  /**
   * What the profile says an anomaly on each indicator MEANS, keyed by
   * indicator. Free text a platform admin writes per use case — a chlorophyll
   * spike means something different on a tuna pen than on a shrimp pond.
   *
   * HERE RATHER THAN IN `indicatorFilter`, where it lived first: that store
   * holds VIEW PREFERENCES (which series are toggled on, the split pair), and
   * this is neither a preference nor filtered by one. It is a plain derivation
   * of `profileIndicators`, two lines above.
   *
   * `null` for a direction the profile leaves blank, which the alert list reads
   * as "say the generic thing" rather than as an empty sentence.
   */
  const anomalyTexts = computed<
    Readonly<Record<string, { high: string | null; low: string | null }>>
  >(() =>
    Object.fromEntries(
      profileIndicators.value.map(
        // `as const` so the pair infers as a TUPLE. Without it TypeScript widens
        // it to `(string | {...})[]` and `Object.fromEntries` loses the value
        // type — today the explicit `computed<...>` annotation happens to pull
        // the inference back, which is a dependency nobody would notice
        // removing.
        (indicator) =>
          [
            indicator.key,
            { high: indicator.anomaly_high_text, low: indicator.anomaly_low_text },
          ] as const,
      ),
    ),
  )
  const impersonatedTenantName = ref<string | null>(null)
  const impersonationExpiresAt = ref<string | null>(null)
  const isImpersonating = computed(() => impersonatedTenantName.value !== null)
  // The tenant every gate decides with (impersonated customer's during a
  // window, null for a tenant-less platform session), and where that tenant's
  // pond map opens. Mirrored from every session response like the rest.
  const effectiveTenantId = ref<string | null>(null)
  const mapRegion = ref<MapRegion | null>(null)
  // The effective tenant of the last session this PAGE LOAD has seen.
  // `undefined` means none yet — the first session is a boot or a first
  // sign-in, not a switch. Deliberately NOT cleared by `clearSession`: a 401
  // followed by signing in as another customer in the same tab IS a switch,
  // and forgetting the previous tenant on the 401 would hide it.
  // A plain variable, not state: nothing renders it.
  let lastKnownTenantId: string | null | undefined
  // Where a hard navigation already under way is going, or null. It dedupes:
  // `impersonate()` both re-reads the session (which notices the switch) and
  // navigates itself, and the second call must not queue a second load behind
  // the first. It also tells the router to stop routing (`isLeaving`): between
  // `location.assign` and the unload the SPA keeps running, and a view pushed
  // in that gap — LoginView's redirect after a re-login as another customer —
  // would render the previous tenant's stores under the new session.
  //
  // ⚠️ NOT a one-shot latch. A `beforeunload` prompt (TeamView, the initial-
  // password dialog) can CANCEL the load, and the page then stays alive holding
  // stale stores. So a later tenant switch re-arms it with its own target, and
  // `resumeLeaving()` lets the router retry the load on the next in-app
  // navigation instead of going silently dead.
  const leavingTo = ref<string | null>(null)
  const isLeaving = computed(() => leavingTo.value !== null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  // True once the one-shot boot probe (checkSession) has resolved. The router
  // guard uses this to probe exactly once on first navigation.
  const bootChecked = ref(false)

  // getters
  const isAuthenticated = computed(() => email.value !== null)
  // Affordance flags, NOT authorisation — see src/lib/capabilities.ts. The
  // decision lives there so it can be tested without a store, a component or a
  // router in the room; these only bind it to the current session's role.
  const canOperate = computed(() => canOperateWithRole(role.value))
  const canAdminister = computed(() => canAdministerWithRole(role.value))
  // ONE getter for three flags rather than three computed properties: the
  // catalogue is meant to grow without this file changing, and a `canHeatmap`
  // per flag would make a fourth module a store edit as well as a template one.
  // Not authorisation — see src/lib/featureAccess.ts.
  const has = (flag: FeatureFlag): boolean => hasFeature(features.value, flag)

  // actions

  /** Mirror a session returned by the backend into local state. */
  function applySession(session: SessionInfo): void {
    email.value = session.email
    role.value = session.role
    mustChangePassword.value = session.must_change_password
    tenantSuspended.value = session.tenant_suspended
    isPlatformAdmin.value = session.is_platform_admin
    features.value = session.features ?? []
    // `?? []` like `features`, and for the same reason: a session response from
    // a backend older than B5 has no `profile` at all, and an undefined here
    // would reach the chart as "this tenant has no indicators".
    profileIndicators.value = session.profile?.indicators ?? []
    const wasImpersonating = impersonatedTenantName.value !== null
    impersonatedTenantName.value = session.impersonated_tenant_name
    impersonationExpiresAt.value = session.impersonation_expires_at
    // `?? null` for a backend older than B1, which sends neither field.
    const tenantId = session.effective_tenant_id ?? null
    effectiveTenantId.value = tenantId
    mapRegion.value = session.map_region ?? null

    // 🔴 THE TENANT CHANGED UNDER A LIVE PAGE: reload. Every tenant-scoped
    // store (ponds, alerts, annotations…) still holds the previous customer's
    // data, and whichever view renders next would show it for a frame — a
    // cross-tenant display, not a cosmetic glitch. ONE mechanism for every way
    // this happens: entering or leaving a window, the window expiring (App.vue
    // re-reads the session), a 403 re-sync, or a re-login as someone else after
    // a 401. Same-tenant sessions (another user of the same customer) do not
    // fire it; their data is the same data.
    const previous = lastKnownTenantId
    lastKnownTenantId = tenantId
    if (previous !== undefined && previous !== tenantId) {
      // Back to the panel when a window closed — explicitly or by expiring —
      // because it is the only screen that means anything to a tenant-less
      // platform account. Everywhere else, the start page.
      const leftWindow =
        wasImpersonating &&
        session.impersonated_tenant_name === null &&
        session.is_platform_admin
      // A NEW switch supersedes whatever load was pending (see `leavingTo`).
      leavingTo.value = null
      leave(leftWindow ? '/admin/tenants' : '/')
    }
  }

  function leave(path: string): void {
    if (leavingTo.value !== null) return
    leavingTo.value = path
    hardNavigate(path)
  }

  /**
   * For the router guard: if a hard navigation is pending, try it again and
   * report true so the guard aborts the in-app one. Retrying rather than only
   * refusing is what keeps a cancelled `beforeunload` from leaving every link
   * on the page dead — the next click asks the prompt again.
   */
  function resumeLeaving(): boolean {
    if (leavingTo.value === null) return false
    hardNavigate(leavingTo.value)
    return true
  }

  /**
   * Verify credentials. On success the backend sets the session cookie and we
   * record the session. On failure the message is surfaced via `error` and the
   * call rejects so the caller can stay on the form.
   */
  async function signIn(emailInput: string, password: string): Promise<void> {
    loading.value = true
    error.value = null
    try {
      applySession(await login(emailInput, password))
    } catch (e) {
      error.value = extractApiError(e, 'Sign-in failed. Please try again.')
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Change the current user's password. On success the backend rotates the
   * session cookie and returns a refreshed session with the forced-change flag
   * cleared; we mirror it so the guard stops confining the user. Rejects on
   * failure (wrong current password, too short) so the caller keeps the form.
   */
  async function changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    applySession(await apiChangePassword(currentPassword, newPassword))
  }

  /**
   * End the session. The network error from a flaky logout is swallowed — the
   * local state is cleared regardless so the UI never gets stuck "logged in".
   *
   * Ends in a FULL page load of /login, not a router push: clearing this store
   * is not enough when every other store still holds the signed-out user's
   * tenant data for whoever signs in next in this tab.
   */
  async function signOut(): Promise<void> {
    try {
      await logout()
    } catch {
      // Logout is best-effort; clear local state no matter what.
    } finally {
      clearSession()
      leave('/login')
    }
  }

  /**
   * One-shot boot probe. Asks the backend who is logged in; leaves the session
   * null on a 401 (logged out). Always flips `bootChecked` so the guard does
   * not probe again.
   */
  /**
   * Re-read the session WITHOUT the boot probe's sign-out semantics (B10).
   *
   * `checkSession` clears the store when the probe fails, which is right for a
   * boot probe: nothing is on screen yet and a failed probe means no session.
   * It is wrong here. This runs off the back of ANY 403 — a role refusal, a
   * suspended tenant — and a transient network failure on the probe would then
   * sign the user out of a page they were using, for a reason unrelated to the
   * request they made. So failure is swallowed and the store is left exactly as
   * it was: the worst case is a UI that keeps offering a module until the next
   * reload, which is where this card started rather than a regression.
   *
   * `bootChecked` is deliberately NOT touched — the router guard owns it.
   */
  async function syncSession(): Promise<void> {
    try {
      applySession(await fetchSession())
    } catch {
      // See above: a failed refresh must not be a logout.
    }
  }

  async function checkSession(): Promise<void> {
    try {
      applySession(await fetchSession())
    } catch {
      clearSession()
    } finally {
      bootChecked.value = true
    }
  }

  /**
   * Enter a customer's account, then re-read the session.
   *
   * 🔴 THE RE-READ IS NOT A REFRESH, IT IS THE POINT. The start route answers
   * 204 and says nothing about who the caller has become; from the next request
   * onwards they ARE the customer — operator rank, no platform flag, the panel
   * shut. Mirroring the old session for even one navigation would leave a menu
   * offering screens that now 403.
   *
   * Rejects on failure with the store untouched, so the caller stays where it is
   * and can show the error against the row it came from.
   */
  async function impersonate(tenantId: string): Promise<void> {
    await apiStartImpersonation(tenantId)
    await checkSession()
    // Normally a no-op: the re-read above already saw the tenant change and
    // left. Kept for the one session it cannot see change — a platform account
    // WITH a tenant entering that same tenant — which must still leave a panel
    // whose every route now 403s.
    leave('/')
  }

  /**
   * Leave the customer's account. NOT a sign-out.
   *
   * The window ends and the platform identity comes back on the same session —
   * logging out would achieve the same thing by throwing away the operator's own
   * session and asking them to type a password again.
   */
  async function stopImpersonating(): Promise<void> {
    await apiStopImpersonation()
    await checkSession()
    // Same belt as `impersonate`: normally the re-read already left.
    leave('/admin/tenants')
  }

  /** Drop local auth state without a network call (used by the 401 interceptor). */
  function clearSession(): void {
    email.value = null
    role.value = null
    mustChangePassword.value = false
    tenantSuspended.value = false
    // Cleared with everything else. A stale `true` here would leave the admin
    // panel in the navigation of a signed-out browser — offering a link whose
    // first request is a 401, and briefly implying the session survived.
    isPlatformAdmin.value = false
    // Emptied, not left as-is: a signed-out browser keeping the last customer's
    // catalogue would render the next sign-in's first frame with THEIR modules.
    features.value = []
    profileIndicators.value = []
    impersonatedTenantName.value = null
    impersonationExpiresAt.value = null
    effectiveTenantId.value = null
    mapRegion.value = null
    error.value = null
  }

  return {
    // state
    email,
    role,
    mustChangePassword,
    tenantSuspended,
    isPlatformAdmin,
    features,
    profileIndicators,
    anomalyTexts,
    impersonatedTenantName,
    impersonationExpiresAt,
    effectiveTenantId,
    mapRegion,
    isLeaving,
    loading,
    error,
    bootChecked,
    // getters
    isAuthenticated,
    isImpersonating,
    canOperate,
    canAdminister,
    has,
    // actions
    signIn,
    impersonate,
    stopImpersonating,
    changePassword,
    signOut,
    checkSession,
    syncSession,
    clearSession,
    resumeLeaving,
  }
})
